/**
 * "Open" on a reminder row: bring the request it came from back on screen IN THE TOOL.
 *
 * A reminder outlives its card by design (the card vanishes when the reply is sent), so opening
 * one has two cases:
 *  - the thread is still in the loaded queue → expand its role group, expand the card, scroll to
 *    it (the card root carries id="ta-request-<threadId>");
 *  - it is gone (answered, archived, or older than the queue window) → fall back to the
 *    mailbox-wide search by candidate name — the same path as typing it into the search box,
 *    which is deliberately NOT limited to the two-week queue window and forces every matching
 *    group open.
 *
 * **Returns whether the card was actually found**, because the fallback can legitimately come up
 * empty: a thread the recruiter removed from the overview is recorded as handled and does not
 * return to the queue, so the search finds nothing and the click looks like a dead button. Callers
 * that can say something useful about that (the scheduled-replies strip points at Gmail instead)
 * use the return value; the reminders strip ignores it, where a plain search is a fine outcome.
 *
 * Its own file for RequestsTab's 500-line cap.
 */
import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { InboundRequest } from '../../types/taActionBoard';

export function useReminderFocus({
  items,
  setOpenRoles,
  setExpanded,
  setQuery,
}: {
  items: InboundRequest[];
  setOpenRoles: Dispatch<SetStateAction<string[]>>;
  setExpanded: (threadId: string | null) => void;
  setQuery: (q: string) => void;
}) {
  return useCallback(
    (threadId: string, candidateName: string | null): boolean => {
      const item = items.find((i) => i.threadId === threadId);
      if (item) {
        const groupKey = item.postingId || 'no-role'; // requestGroups' own key rule
        setOpenRoles((cur) => (cur.includes(groupKey) ? cur : [...cur, groupKey]));
        setExpanded(threadId);
        // After the group/card have rendered open — one frame is not always enough for the
        // accordion's content to be measurable, so a short timeout instead.
        setTimeout(() => {
          document
            .getElementById(`ta-request-${threadId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
        return true;
      }
      if (candidateName) setQuery(candidateName);
      return false;
    },
    [items, setOpenRoles, setExpanded, setQuery]
  );
}
